import { useState } from "react";
import { LifeBuoy, Trash2, Eye, EyeOff, Printer, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import FoldBlock from "./FoldBlock";
import ConfirmModal from "../ConfirmModal";
import PhaseChip from "../PhaseChip";
import TicketPrintModal from "./TicketPrintModal";
import { TICKET_STATUSES } from "../../lib/enums";

/**
 * TicketsBlock — list of support tickets under a customer.
 *
 * Per the customer-profile rule, every row is a <Link to=/tickets/:id>.
 * Inline icon buttons (print / hide-from-client / delete) live next to
 * each row so power users don't need to navigate first.
 */
const STATUS_ORDER = TICKET_STATUSES.map((s) => s.key);

export default function TicketsBlock({ tickets, canEdit, onChanged, defaultOpen = false, previewEditMode = false, isPortal = false, customerId = null, customerName = "" }) {
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [printTicket, setPrintTicket] = useState(null);

  const isClosed = (s) => ["resolved_via_jc", "resolved_virtually", "resolved_external", "resolved", "closed", "cancelled"].includes(s);
  const visible = showClosed ? tickets : tickets.filter((t) => !isClosed(t.status));

  const grouped = {};
  visible.forEach((t) => {
    const k = t.status || "logged";
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(t);
  });
  const ordered = Object.keys(grouped).sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b));
  const closedCount = tickets.filter((t) => isClosed(t.status)).length;

  const del = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await api.delete(`/tickets/${confirmDel.id}`);
      setConfirmDel(null);
      onChanged?.();
    } finally { setBusy(false); }
  };

  const toggleHide = async (ticket) => {
    setBusy(true);
    try {
      await api.put(`/tickets/${ticket.id}`, { ...ticket, hidden_from_client: !ticket.hidden_from_client });
      onChanged?.();
    } finally { setBusy(false); }
  };

  return (
    <FoldBlock
      icon={LifeBuoy}
      iconColor="#f43f5e"
      title="Support tickets"
      count={visible.length}
      defaultOpen={defaultOpen}
      navigateTo={isPortal ? undefined : "/tickets"}
      navigateLabel="Tickets page"
      testId="block-tickets"
      emptyHint={showClosed ? "No tickets." : "No open tickets."}
      headerExtra={
        closedCount > 0 && (
          <label
            className="text-[10px] uppercase tracking-wider text-[#647087] flex items-center gap-1 cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} data-testid="tickets-show-closed" />
            +{closedCount} closed
          </label>
        )
      }
    >
      <div className="space-y-2">
        {ordered.map((status) => (
          <StatusGroup
            key={status}
            status={status}
            items={grouped[status]}
            canEdit={canEdit}
            previewEditMode={previewEditMode}
            isPortal={isPortal}
            // [Iter 290zp.30] MUST forward `customerId` — the inner
            // component references it when building the /portal/tickets/:id
            // link. Missing this prop caused a `ReferenceError:
            // customerId is not defined` that bubbled up to the
            // portal ErrorBoundary as a "This page hit a snag" card
            // the moment a portal user opened Support Tickets with any
            // rows in the list.
            customerId={customerId}
            onPrint={(t) => setPrintTicket(t)}
            onToggleHide={toggleHide}
            onDelete={(t) => setConfirmDel(t)}
          />
        ))}
      </div>

      <ConfirmModal
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={del}
        title="Delete support ticket"
        message={
          <>Permanently delete ticket{" "}
            <b>{confirmDel?.short_id ? `#${confirmDel.short_id} ` : ""}{confirmDel?.title}</b>?</>
        }
        confirmLabel="Delete ticket"
        testId="ticket-delete-confirm"
        busy={busy}
      />

      {printTicket && (
        <TicketPrintModal
          ticket={printTicket}
          customerName={customerName}
          onClose={() => setPrintTicket(null)}
        />
      )}
    </FoldBlock>
  );
}

function StatusGroup({ status, items, canEdit, previewEditMode, isPortal, customerId, onPrint, onToggleHide, onDelete }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-white/5 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/[0.03] transition-colors"
        data-testid={`tickets-group-${status}`}
      >
        <div className="flex items-center gap-2">
          <ChevronRight className={`w-3 h-3 text-[#647087] transition-transform ${open ? "rotate-90" : ""}`} />
          <PhaseChip value={status} />
          <span className="text-xs text-[#647087]">{items.length} {items.length === 1 ? "ticket" : "tickets"}</span>
        </div>
      </button>
      {open && (
        <div className="divide-y divide-white/5">
          {items.map((t) => (
            <div
              key={t.id}
              className="flex items-stretch gap-1 hover:bg-rose-500/5 transition-colors"
              data-testid={`tickets-row-${t.id}`}
            >
              <Link
                // SECURITY-CRITICAL — portal mode (real customer or
                // owner preview) MUST go to /portal/tickets/:id which
                // fetches the scrubbed ticket. Never link to staff
                // /tickets/:id from a portal context.
                to={
                  isPortal
                    ? `/portal/tickets/${t.id}${customerId ? `?customer_id=${customerId}` : ""}`
                    : `/tickets/${t.id}`
                }
                className="flex-1 min-w-0 flex items-center gap-3 px-3 py-2"
                data-testid={`tickets-row-link-${t.id}`}
              >
                <div className="min-w-0 flex-1">
                  {t.short_id && <div className="text-[10px] font-mono text-[#1fb6c1]">#{t.short_id}</div>}
                  <div className="text-sm font-medium truncate">{t.title}</div>
                  <div className="text-[10px] text-[#647087] truncate flex items-center gap-2">
                    {t.priority && <span className="text-[#1fb6c1]">{t.priority}</span>}
                    <span>{t.created_at ? new Date(t.created_at).toLocaleDateString('en-GB') : ""}</span>
                    {t.hidden_from_client && <span className="text-rose-300 inline-flex items-center gap-0.5"><EyeOff className="w-2.5 h-2.5" /> hidden</span>}
                  </div>
                </div>
              </Link>
              {!isPortal && (
                <div className="flex items-center gap-0.5 pr-2 shrink-0">
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); onPrint(t); }}
                    className="hv-btn hv-btn-ghost text-xs px-2 py-1"
                    title="Print / PDF"
                    data-testid={`tickets-row-print-${t.id}`}
                  >
                    <Printer className="w-3.5 h-3.5" />
                  </button>
                  {previewEditMode && canEdit && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); onToggleHide(t); }}
                      className={`hv-btn hv-btn-ghost text-xs px-2 py-1 ${t.hidden_from_client ? "text-emerald-300" : "text-amber-300"}`}
                      title={t.hidden_from_client ? "Show to client" : "Hide from client"}
                      data-testid={`tickets-row-hide-${t.id}`}
                    >
                      {t.hidden_from_client ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    </button>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); onDelete(t); }}
                      className="hv-btn hv-btn-ghost text-xs px-2 py-1 text-rose-300"
                      title="Delete"
                      data-testid={`tickets-row-delete-${t.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
